import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http, formatErr, fmtGBP } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";
import { Plus, Pencil, Trash, FloppyDisk, X, Copy, ShieldCheck, Lock } from "@phosphor-icons/react";

const ACTION_LABELS = { read: "Read", create: "Create", edit: "Edit", delete: "Delete" };
const JOB_ACTION_LABELS = { approve: "Approve", assign: "Assign", start: "Start", complete: "Complete", close: "Close" };

const SECTION_LABELS = {
  dashboard: "Dashboard", jobs: "Jobs", approvals: "Approvals", calendar: "Calendar",
  invoices: "Invoices", repair_items: "Building Repairs", equipment: "Equipment",
  maintenance_companies: "Maintenance Companies", compliance: "Compliance", stores: "Stores",
  brands: "Brands", companies: "Companies", technicians: "Technicians", skills: "Skills",
  users: "Users", invites: "Invites", reports: "Reports", audit_log: "Audit Log", roles: "Roles",
};

function blankForm() {
  return {
    slug: "",
    label: "",
    cloned_from: "",
    permissions: {},
    job_actions: [],
    store_scope: "managed",
    max_approval_amount_gbp: 0,
    is_technician: false,
  };
}

export default function Roles() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["roles"],
    queryFn: () => http.get("/roles").then((r) => r.data),
    retry: false,
  });

  const [modal, setModal] = useState(null); // {mode, slug?}
  const [form, setForm] = useState(blankForm());
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const canCreate = can(user, "roles", "create");
  const canEdit = can(user, "roles", "edit");
  const canDelete = can(user, "roles", "delete");

  const sections = data?.sections || [];
  const actions = data?.actions || ["read", "create", "edit", "delete"];
  const jobActionKeys = data?.job_actions || ["approve", "assign", "start", "complete", "close"];

  const allRoles = useMemo(
    () => [...(data?.builtins || []), ...(data?.customs || [])],
    [data]
  );

  const openCreate = () => {
    setForm(blankForm());
    setModal({ mode: "create" });
    setErr("");
  };
  const openClone = (src) => {
    setForm({
      slug: "",
      label: `${src.label} (copy)`,
      cloned_from: src.slug,
      permissions: JSON.parse(JSON.stringify(src.permissions || {})),
      job_actions: [...(src.job_actions || [])],
      store_scope: src.store_scope || "managed",
      max_approval_amount_gbp: src.max_approval_amount_gbp ?? 0,
      is_technician: !!src.is_technician,
    });
    setModal({ mode: "create" });
    setErr("");
  };
  const openEdit = (src) => {
    setForm({
      slug: src.slug,
      label: src.label,
      cloned_from: src.cloned_from || "",
      permissions: JSON.parse(JSON.stringify(src.permissions || {})),
      job_actions: [...(src.job_actions || [])],
      store_scope: src.store_scope || "managed",
      max_approval_amount_gbp: src.max_approval_amount_gbp ?? 0,
      is_technician: !!src.is_technician,
    });
    setModal({ mode: "edit", slug: src.slug, is_builtin: src.is_builtin });
    setErr("");
  };

  const togglePerm = (section, action) => {
    setForm((prev) => {
      const row = { ...(prev.permissions[section] || {}) };
      row[action] = !row[action];
      // when granting any non-read perm, force read on
      if (action !== "read" && row[action]) row.read = true;
      return { ...prev, permissions: { ...prev.permissions, [section]: row } };
    });
  };
  const toggleJobAction = (a) => {
    setForm((prev) => ({
      ...prev,
      job_actions: prev.job_actions.includes(a)
        ? prev.job_actions.filter((x) => x !== a)
        : [...prev.job_actions, a],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (modal.mode === "create") {
        await http.post("/roles", form);
      } else {
        const { slug, ...patch } = form; // backend doesn't accept slug change on patch
        await http.patch(`/roles/${slug}`, patch);
      }
      qc.invalidateQueries({ queryKey: ["roles"] });
      setModal(null);
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (role) => {
    if (!window.confirm(`Delete custom role "${role.label}"?`)) return;
    try {
      await http.delete(`/roles/${role.slug}`);
      qc.invalidateQueries({ queryKey: ["roles"] });
    } catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };

  const [editLimit, setEditLimit] = useState(null); // {slug, value}

  const saveBuiltinLimit = async () => {
    if (!editLimit) return;
    try {
      await http.patch(`/roles/${editLimit.slug}`, { max_approval_amount_gbp: Number(editLimit.value) });
      qc.invalidateQueries({ queryKey: ["roles"] });
      qc.invalidateQueries({ queryKey: ["role-limits"] });
      setEditLimit(null);
    } catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };

  if (isLoading) return <div className="p-8 text-sm text-slate-500">Loading roles…</div>;
  if (error) return <div className="p-8 text-sm text-rose-600" data-testid="roles-forbidden">You don't have permission to view roles.</div>;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline">Access Control</div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1">Roles & Permissions</h1>
          <p className="text-sm text-slate-500 mt-1">Built-in roles' permissions are locked — but you can override their <span className="font-semibold">approval limit</span> directly. Clone any built-in as a template, or create a new role from scratch.</p>
        </div>
        {canCreate && (
          <button onClick={openCreate} data-testid="role-add-button" className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: "#002FA7" }}>
            <Plus size={14} weight="bold" /> New role
          </button>
        )}
      </div>

      <div className="rmms-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-heading uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="px-4 py-2.5">Role</th>
              <th className="px-4 py-2.5">Type</th>
              <th className="px-4 py-2.5">Store scope</th>
              <th className="px-4 py-2.5">Sections</th>
              <th className="px-4 py-2.5">Job actions</th>
              <th className="px-4 py-2.5 text-right">Approval limit</th>
              <th className="px-4 py-2.5 w-32"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {allRoles.map((r) => {
              const sectionsWithRead = Object.entries(r.permissions || {}).filter(([, p]) => p?.read).length;
              return (
                <tr key={r.slug} data-testid={`role-row-${r.slug}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <ShieldCheck size={14} className={r.is_builtin ? "text-slate-400" : "text-[#002FA7]"} />
                      <div>
                        <div className="font-medium">{r.label}</div>
                        <div className="font-mono-data text-[11px] text-slate-500">{r.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {r.is_builtin ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-heading uppercase tracking-wider font-semibold border rounded-sm px-2 py-0.5 bg-slate-50 text-slate-600 border-slate-200">
                        <Lock size={9} /> Built-in
                      </span>
                    ) : (
                      <span className="text-[11px] font-heading uppercase tracking-wider font-semibold border rounded-sm px-2 py-0.5 bg-blue-50 text-[#002FA7] border-blue-200">Custom</span>
                    )}
                    {r.has_override && <span className="ml-1 text-[10px] font-heading uppercase tracking-wider font-semibold border rounded-sm px-1.5 py-0.5 bg-amber-50 text-amber-700 border-amber-200" data-testid={`role-override-badge-${r.slug}`}>Overridden</span>}
                    {r.is_technician && <span className="ml-1 text-[10px] text-slate-500">tech</span>}
                  </td>
                  <td className="px-4 py-3 text-[12px] text-slate-600">
                    {r.store_scope === "all" ? "All stores" : "Managed stores"}
                  </td>
                  <td className="px-4 py-3 text-[12px] font-mono-data text-slate-600">{sectionsWithRead}</td>
                  <td className="px-4 py-3 text-[12px] text-slate-600">
                    {(r.job_actions || []).length === 0 ? "—" : (r.job_actions || []).map((a) => JOB_ACTION_LABELS[a] || a).join(", ")}
                  </td>
                  <td className="px-4 py-3 text-right font-mono-data tabular-nums text-[12px]">
                    {editLimit?.slug === r.slug ? (
                      <div className="inline-flex items-center gap-1 justify-end">
                        <input
                          type="number" step="0.01"
                          autoFocus
                          value={editLimit.value}
                          onChange={(e) => setEditLimit({ ...editLimit, value: e.target.value })}
                          onKeyDown={(e) => { if (e.key === "Enter") saveBuiltinLimit(); if (e.key === "Escape") setEditLimit(null); }}
                          data-testid={`role-limit-input-${r.slug}`}
                          className="h-7 w-28 px-2 text-right border border-[#002FA7] rounded-sm outline-none"
                        />
                        <button onClick={saveBuiltinLimit} data-testid={`role-limit-save-${r.slug}`} className="h-7 w-7 text-white rounded-sm" style={{ background: "#002FA7" }} title="Save"><FloppyDisk size={12} /></button>
                        <button onClick={() => setEditLimit(null)} className="h-7 w-7 border border-slate-200 rounded-sm" title="Cancel"><X size={12} /></button>
                      </div>
                    ) : (
                      <button
                        onClick={() => canEdit && setEditLimit({ slug: r.slug, value: r.max_approval_amount_gbp === -1 ? -1 : (r.max_approval_amount_gbp || 0) })}
                        disabled={!canEdit}
                        data-testid={`role-limit-display-${r.slug}`}
                        className={`px-2 py-0.5 rounded-sm transition-colors ${canEdit ? "hover:bg-slate-100 cursor-pointer" : "cursor-default"}`}
                        title={canEdit ? "Click to edit" : ""}
                      >
                        {r.max_approval_amount_gbp === -1 ? "Unlimited" : fmtGBP(r.max_approval_amount_gbp)}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      {canCreate && (
                        <button onClick={() => openClone(r)} data-testid={`role-clone-${r.slug}`} title="Clone to create"
                          className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-slate-50"><Copy size={12} /></button>
                      )}
                      {!r.is_builtin && canEdit && (
                        <button onClick={() => openEdit(r)} data-testid={`role-edit-${r.slug}`} title="Edit"
                          className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-slate-50"><Pencil size={12} /></button>
                      )}
                      {r.is_builtin && r.slug !== "admin" && canEdit && (
                        <button onClick={() => openEdit(r)} data-testid={`role-edit-${r.slug}`} title="Edit built-in role"
                          className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-slate-50"><Pencil size={12} /></button>
                      )}
                      {!r.is_builtin && canDelete && (
                        <button onClick={() => remove(r)} data-testid={`role-delete-${r.slug}`} title="Delete"
                          className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-rose-50 hover:text-rose-600"><Trash size={12} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="role-modal">
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md w-full max-w-4xl p-6 space-y-4 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline">{modal.mode === "create" ? "New" : "Edit"}</div>
                <h2 className="text-xl font-heading font-semibold tracking-tight mt-1">{modal.is_builtin ? "Built-in role override" : "Custom role"}</h2>
                {modal.is_builtin && (
                  <div className="text-[11px] mt-1 inline-flex items-center gap-1.5 text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-0.5" data-testid="builtin-warning">
                    <Lock size={9} /> Editing the built-in <span className="font-mono-data">{modal.slug}</span> role — saved as an override.
                  </div>
                )}
                {form.cloned_from && (
                  <div className="text-xs text-slate-500 mt-1">Cloned from <span className="font-mono-data">{form.cloned_from}</span></div>
                )}
              </div>
              <button type="button" onClick={() => setModal(null)} className="h-8 w-8 rounded-sm hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="overline mb-1.5 block">Slug *</label>
                <input
                  required
                  disabled={modal.mode === "edit"}
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  data-testid="role-slug-input"
                  placeholder="e.g. regional_supervisor"
                  className="rmms-input font-mono-data"
                />
              </div>
              <div>
                <label className="overline mb-1.5 block">Display name *</label>
                <input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="role-label-input" className="rmms-input" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Approval limit (GBP)</label>
                <input
                  type="number" step="0.01"
                  value={form.max_approval_amount_gbp}
                  onChange={(e) => setForm({ ...form, max_approval_amount_gbp: Number(e.target.value) })}
                  data-testid="role-limit-input"
                  className="rmms-input font-mono-data"
                />
                <div className="text-[10px] text-slate-500 mt-1">Use <span className="font-mono-data">-1</span> for unlimited.</div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="overline mb-1.5 block">Store scope</label>
                <select value={form.store_scope} onChange={(e) => setForm({ ...form, store_scope: e.target.value })} data-testid="role-store-scope-select" className="rmms-input">
                  <option value="managed">Managed stores only</option>
                  <option value="all">All stores</option>
                </select>
                <div className="text-[10px] text-slate-500 mt-1">Managed = users assigned this role only see stores set on their profile.</div>
              </div>
              <div>
                <label className="overline mb-1.5 block">Technician role?</label>
                <label className="flex items-center gap-2 h-10 cursor-pointer">
                  <input type="checkbox" checked={form.is_technician} onChange={(e) => setForm({ ...form, is_technician: e.target.checked })} data-testid="role-is-tech-checkbox" />
                  <span className="text-sm">User only sees their own assigned jobs</span>
                </label>
              </div>
            </div>

            <div>
              <label className="overline mb-2 block">Job lifecycle actions</label>
              <div className="flex flex-wrap gap-2">
                {jobActionKeys.map((a) => (
                  <button key={a} type="button" onClick={() => toggleJobAction(a)} data-testid={`role-jobaction-${a}`}
                    className={`text-[11px] px-3 py-1.5 border rounded-sm transition-colors ${
                      form.job_actions.includes(a) ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-slate-200 hover:bg-slate-50"
                    }`}>
                    {JOB_ACTION_LABELS[a] || a}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="overline mb-2 block">Section permissions</label>
              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-[10px] font-heading uppercase tracking-wider text-slate-500">
                      <th className="px-3 py-2 text-left">Section</th>
                      {actions.map((a) => <th key={a} className="px-3 py-2 text-center w-20">{ACTION_LABELS[a] || a}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sections.map((s) => (
                      <tr key={s}>
                        <td className="px-3 py-2 text-sm">{SECTION_LABELS[s] || s}</td>
                        {actions.map((a) => (
                          <td key={a} className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={!!form.permissions?.[s]?.[a]}
                              onChange={() => togglePerm(s, a)}
                              data-testid={`role-perm-${s}-${a}`}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Granting Create/Edit/Delete automatically enables Read.</div>
            </div>

            {err && <div className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1" data-testid="role-modal-error">{err}</div>}

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <button type="button" onClick={() => setModal(null)} className="h-10 px-4 text-sm border border-slate-200 rounded-md">Cancel</button>
              <button type="submit" disabled={busy} data-testid="role-submit-button" className="h-10 px-4 rounded-md text-white text-sm inline-flex items-center gap-1.5" style={{ background: "#002FA7" }}>
                <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save role"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
